import { useState, useEffect, useRef, useCallback } from "react"
// import Value from "aldoo-ra/Value" // Uncomment when reverting to site language
import { GetHelpCenterTOC, GetArticle, GetArticleInCategory } from "../api"
import { usePromise } from "aldoo-ra/usePromise"

/**
 * Custom hook for loading content based on URL with language support
 * Uses TOC for navigation but still makes API calls for article content
 */
function useContentLoader() {
  // Force English for help center
  const [currentLanguage] = useState("en")
  
  // To revert to site language, uncomment below and comment out the line above:
  // const [currentLanguage] = Value({
  //   key: "current-site-language",
  //   defaultValue: "en",
  //   persistKey: "current-site-language",
  // })

  const lastLoadedLanguage = useRef("en")

  const [selectedContent, setSelectedContent] = useState(null)
  const [contentType, setContentType] = useState(null)
  const [error, setError] = useState(null)

  const [rootCategories, setRootCategories] = useState(null)
  const [expandedCategories, setExpandedCategories] = useState({})
  const [activeContentSlug, setActiveContentSlug] = useState(null)
  const [activeContentType, setActiveContentType] = useState(null)
  const [breadcrumbItems, setBreadcrumbItems] = useState([
    { label: "Help Center", link: "/help-center" },
  ])

  const [browserInitialized, setBrowserInitialized] = useState(false)
  const [categoriesLoaded, setCategoriesLoaded] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const initRef = useRef(false)

  const activeContentRef = useRef({
    type: null,
    slug: null,
    categoryPath: null,
  })

  const getTocPromise = useCallback(
    async (language = currentLanguage) => {
      const response = await GetHelpCenterTOC(language)
      if (response && response.success && response.toc) {
        return response.toc
      }
      throw new Error("Failed to load help center structure")
    },
    [currentLanguage]
  )

  const {
    result: tocData,
    pending: isTocLoading,
    error: tocError,
    execute: executeTocLoad,
    refresh: refreshToc,
  } = usePromise(getTocPromise, true)

  const isLoading = Boolean(isTocLoading)

  const combinedError = error || tocError

  const parseUrlPath = useCallback(() => {
    if (typeof window === "undefined") {
      return { type: null, categoryPath: null, articleSlug: null }
    }

    const path = window.location.pathname
    const helpCenterPath = "/help-center"

    if (!path.startsWith(helpCenterPath)) {
      return { type: null, categoryPath: null, articleSlug: null }
    }

    // Special handling for submit-request - don't parse it as help center content
    if (path === "/help-center/submit-request") {
      return { type: null, categoryPath: null, articleSlug: null }
    }

    const relativePath = path.substring(helpCenterPath.length)

    if (!relativePath || relativePath === "/") {
      if (typeof setExpandedCategories === "function") {
        setExpandedCategories({})
      }
      return { type: null, categoryPath: null, articleSlug: null }
    }

    const segments = relativePath.split("/").filter(Boolean)

    if (
      segments.length >= 3 &&
      segments[0] === "category" &&
      segments.includes("article")
    ) {
      const articleIndex = segments.indexOf("article")
      const categorySegments = segments.slice(1, articleIndex)
      const articleSlug = segments[articleIndex + 1]

      return {
        type: "article",
        categoryPath: categorySegments.join("/"),
        articleSlug,
      }
    } else if (segments.length >= 2 && segments[0] === "article") {
      return {
        type: "article",
        categoryPath: null,
        articleSlug: segments[1],
      }
    } else if (segments.length >= 2 && segments[0] === "category") {
      return {
        type: "category",
        categoryPath: segments.slice(1).join("/"),
        articleSlug: null,
      }
    }

    return { type: null, categoryPath: null, articleSlug: null }
  }, [setExpandedCategories])

  const findArticleInToc = useCallback(
    (slug, categoryPath = null) => {
      if (!tocData || !tocData.content || !tocData.content.categories) {
        return null
      }

      let categories = tocData.content.categories
      let breadcrumbData = []
      let foundArticle = null

      if (categoryPath) {
        const pathParts = categoryPath.split("/")
        let currentPath = ""

        for (let i = 0; i < pathParts.length; i++) {
          const part = pathParts[i]
          currentPath = i === 0 ? part : `${currentPath}/${part}`

          const catLabel =
            part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " ")
          const previousPath = i > 0 ? pathParts.slice(0, i).join("/") : ""

          breadcrumbData.push({
            label: catLabel,
            link: `/help-center/category/${currentPath}`,
            categorySlug: part,
            parentPath: previousPath,
          })
        }

        let currentCategories = categories
        for (const part of pathParts) {
          const nextCategory = currentCategories.find((c) => c.slug === part)
          if (!nextCategory) {
            return null
          }

          if (nextCategory.articles) {
            foundArticle = nextCategory.articles.find((a) => a.slug === slug)
            if (foundArticle) break
          }

          if (!nextCategory.categories) {
            break
          }

          currentCategories = nextCategory.categories
        }

        if (!foundArticle && currentCategories) {
          for (const cat of currentCategories) {
            if (cat.articles) {
              foundArticle = cat.articles.find((a) => a.slug === slug)
              if (foundArticle) break
            }
          }
        }
      } else {
        const searchInCategories = (categories, path = []) => {
          for (const cat of categories) {
            const currentPath = [...path, cat]

            if (cat.articles) {
              const article = cat.articles.find((a) => a.slug === slug)
              if (article) {
                breadcrumbData = currentPath.map((category, index) => {
                  const pathSoFar = currentPath
                    .slice(0, index + 1)
                    .map((c) => c.slug)
                    .join("/")
                  return {
                    label: category.label,
                    link: `/help-center/category/${pathSoFar}`,
                    categorySlug: category.slug,
                    parentPath:
                      index > 0
                        ? currentPath
                            .slice(0, index)
                            .map((c) => c.slug)
                            .join("/")
                        : "",
                  }
                })
                return { article, breadcrumbData }
              }
            }

            if (cat.categories && cat.categories.length > 0) {
              const result = searchInCategories(cat.categories, currentPath)
              if (result) return result
            }
          }
          return null
        }

        const result = searchInCategories(categories)
        if (result) {
          foundArticle = result.article
          breadcrumbData = result.breadcrumbData
        }
      }

      if (!foundArticle) {
        return null
      }

      return { articleMetadata: foundArticle, breadcrumbData }
    },
    [tocData]
  )

  const findCategoryInToc = useCallback(
    (categoryPath) => {
      if (
        !tocData ||
        !tocData.content ||
        !tocData.content.categories ||
        !categoryPath
      ) {
        return null
      }

      const pathParts = categoryPath.split("/")
      let currentCategories = tocData.content.categories
      let currentCategory = null
      let breadcrumbData = []
      let currentPath = ""

      for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i]
        currentPath = i === 0 ? part : `${currentPath}/${part}`

        currentCategory = currentCategories.find((c) => c.slug === part)
        if (!currentCategory) {
          return null
        }

        const catLabel =
          currentCategory.label ||
          part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " ")
        const previousPath = i > 0 ? pathParts.slice(0, i).join("/") : ""

        if (i === pathParts.length - 1) {
          breadcrumbData.push({ label: catLabel })
        } else {
          breadcrumbData.push({
            label: catLabel,
            link: `/help-center/category/${currentPath}`,
            categorySlug: part,
            parentPath: previousPath,
          })
        }

        if (currentCategory.categories) {
          currentCategories = currentCategory.categories
        } else if (i < pathParts.length - 1) {
          return null
        }
      }

      if (!currentCategory) {
        return null
      }

      const categoryContent = {
        content: {
          type: "HCCategory",
          name: currentCategory.label,
          slug: currentCategory.slug,
          categories: currentCategory.categories || [],
          articles: currentCategory.articles || [],
          notes: currentCategory.notes || [],
          icon: currentCategory.icon,
        },
        _id: currentCategory._id,
        path: categoryPath,
        breadcrumbData: breadcrumbData,
      }

      return { categoryContent, breadcrumbData }
    },
    [tocData]
  )

  const buildExpansionMap = useCallback(
    (categoryPath) => {
      if (!categoryPath) return {}

      const pathParts = categoryPath.split("/")
      const newExpandedCategories = { ...expandedCategories }

      let cumulativePath = ""
      for (const slug of pathParts) {
        cumulativePath = cumulativePath ? `${cumulativePath}/${slug}` : slug
        newExpandedCategories[cumulativePath] = true
        newExpandedCategories[slug] = true
      }

      return newExpandedCategories
    },
    [expandedCategories]
  )

  const loadArticle = useCallback(
    async (slug, categoryPath = null) => {
      if (!slug || !tocData) {
        return null
      }

      setError(null)

      try {
        activeContentRef.current = {
          type: "article",
          slug,
          categoryPath,
        }

        if (categoryPath && categoryPath.includes("/")) {
          const expansionMap = buildExpansionMap(categoryPath)
          setExpandedCategories(expansionMap)
        }

        const result = findArticleInToc(slug, categoryPath)

        if (!result) {
          throw new Error(
            `Article not found: ${slug} in ${categoryPath || "root"}`
          )
        }

        const { articleMetadata, breadcrumbData } = result

        let articleData
        if (categoryPath) {
          articleData = await GetArticleInCategory(categoryPath, slug)
        } else {
          articleData = await GetArticle(slug)
        }

        const newBreadcrumbs = [
          { label: "Help Center", link: "/help-center" },
          ...breadcrumbData,
        ]

        if (
          newBreadcrumbs[newBreadcrumbs.length - 1].label !==
          articleData.content.name
        ) {
          newBreadcrumbs.push({
            label: articleData.content.name,
          })
        }

        setBreadcrumbItems(newBreadcrumbs)
        setSelectedContent(articleData)
        setContentType("article")
        setActiveContentSlug(slug)
        setActiveContentType("article")

        lastLoadedLanguage.current = currentLanguage

        return articleData
      } catch (err) {
        console.error("loadArticle error:", err)
        setError("Failed to load article. Please try again later.")
        return null
      }
    },
    [buildExpansionMap, currentLanguage, findArticleInToc, tocData]
  )

  const loadCategory = useCallback(
    async (categoryPath) => {
      if (!categoryPath || !tocData) return null

      setError(null)

      try {
        activeContentRef.current = {
          type: "category",
          slug: categoryPath,
          categoryPath: null,
        }

        if (categoryPath.includes("/")) {
          const expansionMap = buildExpansionMap(categoryPath)
          setExpandedCategories(expansionMap)
        }

        const result = findCategoryInToc(categoryPath)
        if (!result) {
          throw new Error(`Category not found: ${categoryPath}`)
        }

        const { categoryContent, breadcrumbData } = result

        const newBreadcrumbs = [
          { label: "Help Center", link: "/help-center" },
          ...breadcrumbData,
        ]

        setBreadcrumbItems(newBreadcrumbs)
        setSelectedContent(categoryContent)
        setContentType("category")
        setActiveContentSlug(categoryPath)
        setActiveContentType("category")

        lastLoadedLanguage.current = currentLanguage

        return categoryContent
      } catch (err) {
        setError("Failed to load category. Please try again later.")
        return null
      }
    },
    [buildExpansionMap, currentLanguage, findCategoryInToc, tocData]
  )

  const loadContentFromUrl = useCallback(async () => {
    if (!tocData) {
      return
    }

    setError(null)

    try {
      const urlParams = parseUrlPath()

      if (
        !urlParams.type &&
        !urlParams.categoryPath &&
        !urlParams.articleSlug
      ) {
        setExpandedCategories({})
        setSelectedContent(null)
        setContentType(null)
        setBreadcrumbItems([{ label: "Help Center", link: "/help-center" }])
        setActiveContentSlug(null)
        setActiveContentType(null)

        activeContentRef.current = {
          type: null,
          slug: null,
          categoryPath: null,
        }
      } else if (urlParams.categoryPath) {
        const expansionMap = buildExpansionMap(urlParams.categoryPath)
        setExpandedCategories(expansionMap)
      }

      if (urlParams.type === "article") {
        await loadArticle(urlParams.articleSlug, urlParams.categoryPath)
      } else if (urlParams.type === "category") {
        await loadCategory(urlParams.categoryPath)
      }

      lastLoadedLanguage.current = currentLanguage
    } catch (err) {
      setError("Failed to load content. Please try again later.")
    }
  }, [
    parseUrlPath,
    buildExpansionMap,
    loadArticle,
    loadCategory,
    currentLanguage,
    tocData,
  ])

  const refreshContent = useCallback(async () => {
    setError(null)

    try {
      let loadMethod

      if (typeof refreshToc === "function") {
        loadMethod = refreshToc
      } else if (typeof executeTocLoad === "function") {
        loadMethod = executeTocLoad
      } else {
        loadMethod = async (language) => {
          const response = await GetHelpCenterTOC(language)
          if (response && response.success && response.toc) {
            return response.toc
          }
          throw new Error("Failed to load help center structure")
        }
      }

      const result = await loadMethod(currentLanguage)

      const currentPath = window.location.pathname

      const isHomePage = currentPath === "/help-center"

      if (isHomePage) {
        setExpandedCategories({})
        setActiveContentSlug(null)
        setActiveContentType(null)
        setBreadcrumbItems([{ label: "Help Center", link: "/help-center" }])
        setSelectedContent(null)
        setContentType(null)

        activeContentRef.current = {
          type: null,
          slug: null,
          categoryPath: null,
        }
      } else {
        await loadContentFromUrl()
      }

      lastLoadedLanguage.current = currentLanguage
    } catch (err) {
      setError(`Failed to refresh content: ${err.message}`)
    }
  }, [currentLanguage, loadContentFromUrl, refreshToc, executeTocLoad])

  // Hydration effect - must run first
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  useEffect(() => {
    if (!tocData && !isTocLoading) {
      executeTocLoad(currentLanguage)
    }
  }, [])

  // Ensure we initialize properly when navigating directly to /help-center
  useEffect(() => {
    if (!isHydrated || typeof window === "undefined") return

    const path = window.location.pathname
    const isHelpCenterHome = path === "/help-center" || path === "/help-center/"

    if (isHelpCenterHome && !selectedContent && !contentType) {
      // Force home page state even if TOC isn't loaded yet
      setSelectedContent(null)
      setContentType(null)
      setBreadcrumbItems([{ label: "Help Center", link: "/help-center" }])
      setActiveContentSlug(null)
      setActiveContentType(null)
      setExpandedCategories({})

      activeContentRef.current = {
        type: null,
        slug: null,
        categoryPath: null,
      }
    }
  }, [isHydrated, selectedContent, contentType])

  useEffect(() => {
    if (tocData && tocData.content && tocData.content.categories) {
      setRootCategories(tocData.content.categories)
      setCategoriesLoaded(true)
    }
  }, [tocData])

  useEffect(() => {
    if (!initRef.current && tocData) {
      initRef.current = true

      // Don't load content from URL if we're on submit-request page
      const currentUrl =
        typeof window !== "undefined" ? window.location.pathname : ""
      if (currentUrl === "/help-center/submit-request") {
        setBrowserInitialized(true)
        return
      }

      loadContentFromUrl().then(() => {
        setBrowserInitialized(true)
      })
    }
  }, [tocData, loadContentFromUrl])

  useEffect(() => {
    if (browserInitialized && lastLoadedLanguage.current !== currentLanguage) {
      refreshContent()
    }
  }, [currentLanguage, browserInitialized, refreshContent])

  // Comprehensive URL change handler that works for all navigation types
  useEffect(() => {
    if (!isHydrated || !tocData) return

    let lastUrl = window.location.pathname
    let isProcessingUrlChange = false
    let debounceTimer = null

    const handleUrlChange = async () => {
      if (isProcessingUrlChange) return

      // Clear any pending debounced calls
      if (debounceTimer) {
        clearTimeout(debounceTimer)
        debounceTimer = null
      }

      // Skip if this was a programmatic navigation
      if (window.skipNextHelpCenterUrlChange) {
        window.skipNextHelpCenterUrlChange = false
        return
      }

      const currentUrl = window.location.pathname

      // Only handle URLs that belong to the main Help Center content
      // Don't handle URLs that belong to other components (like submit-request)
      if (
        !currentUrl.startsWith("/help-center") ||
        currentUrl === "/help-center/submit-request"
      ) {
        return
      }

      isProcessingUrlChange = true

      try {
        const shouldBeHomePage =
          currentUrl === "/help-center" || currentUrl === "/help-center/"

        if (shouldBeHomePage) {
          // Reset to home page state immediately
          setSelectedContent(null)
          setContentType(null)
          setBreadcrumbItems([{ label: "Help Center", link: "/help-center" }])
          setActiveContentSlug(null)
          setActiveContentType(null)
          setExpandedCategories({})

          activeContentRef.current = {
            type: null,
            slug: null,
            categoryPath: null,
          }
        } else {
          // Parse URL and load appropriate content

          // Parse URL inline to avoid stale closure
          const path = currentUrl
          const helpCenterPath = "/help-center"

          if (!path.startsWith(helpCenterPath)) {
            return
          }

          const relativePath = path.substring(helpCenterPath.length)

          if (!relativePath || relativePath === "/") {
            setExpandedCategories({})
            setSelectedContent(null)
            setContentType(null)
            setBreadcrumbItems([{ label: "Help Center", link: "/help-center" }])
            setActiveContentSlug(null)
            setActiveContentType(null)
            return
          }

          const segments = relativePath.split("/").filter(Boolean)
          let urlParams = { type: null, categoryPath: null, articleSlug: null }

          if (
            segments.length >= 3 &&
            segments[0] === "category" &&
            segments.includes("article")
          ) {
            const articleIndex = segments.indexOf("article")
            const categorySegments = segments.slice(1, articleIndex)
            const articleSlug = segments[articleIndex + 1]

            urlParams = {
              type: "article",
              categoryPath: categorySegments.join("/"),
              articleSlug,
            }
          } else if (segments.length >= 2 && segments[0] === "article") {
            urlParams = {
              type: "article",
              categoryPath: null,
              articleSlug: segments[1],
            }
          } else if (segments.length >= 2 && segments[0] === "category") {
            urlParams = {
              type: "category",
              categoryPath: segments.slice(1).join("/"),
              articleSlug: null,
            }
          }

          if (urlParams.type === "article" && urlParams.articleSlug) {
            await loadArticle(urlParams.articleSlug, urlParams.categoryPath)
          } else if (urlParams.type === "category" && urlParams.categoryPath) {
            await loadCategory(urlParams.categoryPath)
          } else {
            setSelectedContent(null)
            setContentType(null)
            setBreadcrumbItems([{ label: "Help Center", link: "/help-center" }])
            setActiveContentSlug(null)
            setActiveContentType(null)
          }
        }
      } catch (error) {
        console.error("Error handling URL change:", error)
        setError("Failed to load content")
      } finally {
        isProcessingUrlChange = false
      }
    }

    // Debounced URL change handler for polling
    const debouncedUrlChange = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
      debounceTimer = setTimeout(handleUrlChange, 100)
    }

    // Use a more conservative polling approach
    const urlMonitor = setInterval(() => {
      const currentUrl = window.location.pathname
      // Only monitor URLs that this component should handle
      if (
        currentUrl.startsWith("/help-center") &&
        currentUrl !== "/help-center/submit-request" &&
        lastUrl !== currentUrl
      ) {
        lastUrl = currentUrl
        debouncedUrlChange()
      }
    }, 1000) // Less aggressive - check every 1 second

    // Handle browser navigation events
    const handlePopState = (event) => {
      // Only handle if this component should manage the URL
      const currentUrl = window.location.pathname
      if (
        currentUrl.startsWith("/help-center") &&
        currentUrl !== "/help-center/submit-request"
      ) {
        // Update lastUrl to prevent double processing
        lastUrl = currentUrl
        // Small delay to ensure URL has fully updated
        setTimeout(handleUrlChange, 50)
      }
    }

    // Listen for page visibility changes (when user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const currentUrl = window.location.pathname
        if (
          currentUrl.startsWith("/help-center") &&
          currentUrl !== "/help-center/submit-request" &&
          lastUrl !== currentUrl
        ) {
          lastUrl = currentUrl
          setTimeout(handleUrlChange, 100)
        }
      }
    }

    window.addEventListener("popstate", handlePopState)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      clearInterval(urlMonitor)
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
      window.removeEventListener("popstate", handlePopState)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [isHydrated, tocData])

  const updateUrl = useCallback((type, slug, categoryPath = "") => {
    if ((type === "article" || type === "category") && !slug) {
      return
    }

    let url = "/help-center"

    if (type === "article") {
      if (categoryPath) {
        url += `/category/${categoryPath}/article/${slug}`
      } else {
        url += `/article/${slug}`
      }
    } else if (type === "category") {
      url += `/category/${categoryPath ? `${categoryPath}/` : ""}${slug}`
    }

    if (window.location.pathname !== url) {
      // Set flag to skip the next URL change handler trigger
      window.skipNextHelpCenterUrlChange = true

      window.history.pushState(
        {
          type,
          slug,
          categoryPath,
        },
        "",
        url
      )

      // Notify HCHome component of the URL change
      window.dispatchEvent(new CustomEvent("helpCenterNavigate"))
    }
  }, [])

  const expandCategoryPath = useCallback(
    (path) => {
      if (!path) return
      const expansionMap = buildExpansionMap(
        typeof path === "string" ? path : path.join("/")
      )
      setExpandedCategories(expansionMap)
    },
    [buildExpansionMap]
  )

  const loadToc = useCallback(() => {
    executeTocLoad(currentLanguage)
  }, [executeTocLoad, currentLanguage])

  return {
    selectedContent,
    contentType,
    isLoading,
    error: combinedError,

    tocData,

    rootCategories,
    expandedCategories,
    activeContentSlug,
    activeContentType,
    breadcrumbItems,
    browserInitialized,
    categoriesLoaded: !!tocData,
    currentLanguage,

    setExpandedCategories,
    setActiveContentSlug,
    setActiveContentType,
    setBreadcrumbItems,
    setSelectedContent,
    setContentType,
    setError,

    loadArticle,
    loadCategory,
    parseUrlPath,
    buildExpansionMap,
    updateUrl,
    loadContentFromUrl,
    expandCategoryPath,
    loadToc,

    refreshContent,
  }
}

export default useContentLoader
